import { useEffect, useState } from "react";
import { Box, Heading, SimpleGrid, Spinner, Text } from "@chakra-ui/react";
import {
  Bar,
  BarChart,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import axiosInstance from "../../config/axiosInstance";
import dayjs from "dayjs";

const COLORS = ["#8884d8", "#82ca9d", "#ffc658", "#ff8042", "#8dd1e1"];

/**
 * ✅ Accepts fromDate & toDate as props (sent by AdminMain)
 * so data can be filtered by date range.
 */
const AdminGraph = ({ fromDate, toDate }) => {
  const [loading, setLoading] = useState(true);
  const [revenueData, setRevenueData] = useState([]);
  const [orderTypeData, setOrderTypeData] = useState([]);
  const [itemEarnings, setItemEarnings] = useState([]);
  const [topItems, setTopItems] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        const params = {};
        if (fromDate && toDate) {
          params.fromDate = dayjs(fromDate).format("YYYY-MM-DD");
          params.toDate = dayjs(toDate).format("YYYY-MM-DD");
        }

        // 1️⃣ Dashboard stats
        const statsRes = await axiosInstance.get(`/api/orders/stats/admin`, {
          params,
        });
        const stats = statsRes.data;

        // 2️⃣ Revenue stats
        const amountRes = await axiosInstance.get(`/api/orders/payment-stats`, {
          params,
        });
        const amountStats = amountRes.data;

        // 3️⃣ Item earnings
        const itemRes = await axiosInstance.get(
          `/api/orders/item-earnings-stats`,
          { params }
        );
        const itemStats = itemRes.data.earnings || [];

        // 4️⃣ Top ordered items
        const topItemRes = await axiosInstance.get(
          `/api/admin-stats/order-stats`,
          { params }
        );
        const topItemStats = topItemRes.data;

        // 📊 Prepare chart data
        const revenue = [
          { name: "Total", items: amountStats.totalAmount || 0 },
          { name: "Paid", items: amountStats.totalPaid || 0 },
          { name: "Pending", items: amountStats.totalPending || 0 },
        ];

        const orderType = [
          { name: "Completed", value: stats.totalDelivered || 0 },
          { name: "Pending", value: stats.totalPending || 0 },
          // { name: "Assigned", value: stats.totalAssigned || 0 },
          { name: "Total", value: stats.totalOrders || 0 },
          { name: "Partial", value: stats.totalPartial || 0 },
        ];

        setRevenueData(revenue);
        setOrderTypeData(orderType);
        setItemEarnings(itemStats);
        setTopItems(topItemStats.topItems || []);
      } catch (err) {
        console.error("Admin Graph API Error:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [fromDate, toDate]); // ✅ refetch whenever date range changes

  if (loading) {
    return (
      <Box w="100%" textAlign="center" py={10}>
        <Spinner size="xl" />
      </Box>
    );
  }

  return (
    <>
      {/* === FIRST ROW === */}
      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={8} mb={8}>
        {/* Revenue Chart */}
        <Box bg="white" p={4} rounded="lg" shadow="sm">
          <Heading size="sm" mb={4}>
            Revenue (Total / Paid / Pending)
          </Heading>
          <ResponsiveContainer width="100%" height={350}>
            <BarChart
              data={revenueData}
              margin={{ top: 20, right: 20, bottom: 50, left: 20 }}
            >
              <XAxis dataKey="name" angle={-45} textAnchor="end" interval={0} />
              <YAxis />
              <Tooltip />
              <Bar dataKey="items" fill="#8884d8" />
            </BarChart>
          </ResponsiveContainer>
        </Box>

        {/* Order Type Pie */}
        <Box bg="white" p={4} rounded="lg" shadow="sm">
          <Heading size="sm" mb={4}>
            Type of Orders
          </Heading>

          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={orderTypeData}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                outerRadius={100}
                label
              >
                {orderTypeData.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={COLORS[index % COLORS.length]}
                  />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>

          {/* ✅ Description / Legend */}
          <Box mt={4}>
            {orderTypeData.map((entry, index) => (
              <Box key={entry.name} display="flex" alignItems="center" mb={1}>
                <Box
                  w="14px"
                  h="14px"
                  borderRadius="full"
                  bg={COLORS[index % COLORS.length]}
                  mr={2}
                />
                <Text fontSize="sm">
                  {entry.name}: <strong>{entry.value}</strong>
                </Text>
              </Box>
            ))}
          </Box>
        </Box>
      </SimpleGrid>

      {/* === SECOND ROW === */}
      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={8}>
        {/* Item-wise Earnings */}
        <Box bg="white" p={4} rounded="lg" shadow="sm">
          <Heading size="sm" mb={4}>
            Earnings by Item
          </Heading>
          {itemEarnings.length === 0 ? (
            <Text>No Data Available</Text>
          ) : (
            <ResponsiveContainer width="100%" height={350}>
              <BarChart
                data={itemEarnings.map((it) => ({
                  name: it.item || "Unknown",
                  earnings: it.totalPaid,
                }))}
                margin={{ top: 20, right: 20, bottom: 50, left: 20 }}
              >
                <XAxis
                  dataKey="name"
                  angle={-45}
                  textAnchor="end"
                  interval={0}
                />
                <YAxis />
                <Tooltip />
                <Bar dataKey="earnings" fill="#82ca9d" />
              </BarChart>
            </ResponsiveContainer>
          )}
        </Box>

        {/* Top Ordered Items */}
        <Box bg="white" p={4} rounded="lg" shadow="sm">
          <Heading size="sm" mb={4}>
            Top Ordered Items
          </Heading>
          {topItems.length === 0 ? (
            <Text>No Data Available</Text>
          ) : (
            <ResponsiveContainer width="100%" height={350}>
              <BarChart
                data={topItems.map((it) => ({
                  name: it.label,
                  count: it.count,
                }))}
                margin={{ top: 20, right: 20, bottom: 50, left: 20 }}
              >
                <XAxis
                  dataKey="name"
                  angle={-45}
                  textAnchor="end"
                  interval={0}
                />
                <YAxis />
                <Tooltip />
                <Bar dataKey="count" fill="#ff8042" />
              </BarChart>
            </ResponsiveContainer>
          )}
        </Box>
      </SimpleGrid>
    </>
  );
};

export default AdminGraph;
